import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../security/AuthContext';

const ProfilePhotoUploadComponent = () => {
  const navigate = useNavigate();
  const authContext = useAuth();
  
  // Get auth info
  const token = authContext.token;
  const memberType = authContext.memberTypeId;

  // State declarations
  const [image, setImage] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [photoCount, setPhotoCount] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [lastPosition, setLastPosition] = useState({ x: 0, y: 0 });
  
  // Refs
  const canvasRef = useRef(null);

  const photoLimits = {
    '1': 10,  // Free member
    '2': 20,  // Upgraded member
    '5': 20   // Premium member
  };

  // Helper function for auth header
  const getAuthHeader = () => {
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const fetchPhotoCount = async () => {
    try {
      setError(null);

      if (!token) {
        console.log('No token available');
        navigate('/login');
        return;
      }

      const response = await fetch('https://info.kissdatelove.com/rest/profile/PhotosCount', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...getAuthHeader()
        }
      });

      console.log('PhotoCount response status:', response.status);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          authContext.logout();
          navigate('/login');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('PhotoCount data:', data);
      
      if (!data || !data.photos || typeof data.photos.PHOTO_COUNT !== 'number') {
        throw new Error('Invalid response format');
      }

      setPhotoCount(data.photos.PHOTO_COUNT);
    } catch (error) {
      console.error('Error fetching photo count:', error);
      setError('Failed to load photo count. Please try again later.');
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files?.[0]) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImage(e.target.result);
        setPosition({ x: 0, y: 0 });
        setScale(1);
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setLastPosition({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - lastPosition.x;
    const newY = e.clientY - lastPosition.y;
    
    // Limit the dragging area
    const maxOffset = 200;
    const clampedX = Math.max(-maxOffset, Math.min(maxOffset, newX));
    const clampedY = Math.max(-maxOffset, Math.min(maxOffset, newY));
    
    setPosition({
      x: clampedX,
      y: clampedY
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (!token) {
      console.log('No token - redirecting to login');
      navigate('/login');
      return;
    }
    
    fetchPhotoCount();
  }, [token, navigate]);

  // NEW IMAGE PROCESSING EFFECT - Copied and adapted from SignupStep2Component
  useEffect(() => {
    if (!image || !canvasRef.current) return;
  
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Set a dark gray background for the canvas (for UI only)
      ctx.fillStyle = '#333333';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Calculate the center point of the canvas
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      
      // Calculate the crop circle radius (slightly smaller than the canvas width/2)
      const radius = Math.min(canvas.width, canvas.height) / 2 - 10;
      
      // Calculate dimensions maintaining aspect ratio
      let drawWidth, drawHeight;
      const imgAspect = img.width / img.height;
      
      if (imgAspect >= 1) {
        // Landscape or square image
        drawHeight = canvas.height * scale;
        drawWidth = drawHeight * imgAspect;
      } else {
        // Portrait image
        drawWidth = canvas.width * scale;
        drawHeight = drawWidth / imgAspect;
      }
      
      // Calculate position to center the image while allowing for dragging
      const drawX = centerX - (drawWidth / 2) + position.x;
      const drawY = centerY - (drawHeight / 2) + position.y;
      
      // Save context for display version
      ctx.save();
      
      // Create a clipping path for the circle (for display only)
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2, false);
      ctx.clip();
      
      // Draw the image inside the clipped circle
      ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
      
      // Restore the context to remove the clipping
      ctx.restore();
      
      // Draw a white circle border for the UI
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2, true);
      ctx.stroke();
      
      // COMPLETELY SEPARATE APPROACH FOR PREVIEW
      // Create a new canvas for the square crop
      const squareCanvas = document.createElement('canvas');
      const squareCtx = squareCanvas.getContext('2d');
      
      // Set dimensions for square crop preview
      const cropSize = radius * 2;
      squareCanvas.width = cropSize;
      squareCanvas.height = cropSize;
      
      // Draw the original image directly onto the square canvas
      // This bypasses the circular mask completely
      
      // Calculate the source area from the original image
      // These are the coordinates in the original image that correspond to our crop area
      const sourceX = (centerX - radius - drawX) * (img.width / drawWidth);
      const sourceY = (centerY - radius - drawY) * (img.height / drawHeight);
      const sourceWidth = cropSize * (img.width / drawWidth);
      const sourceHeight = cropSize * (img.height / drawHeight);
      
      // Draw the relevant portion of the original image onto our square canvas
      squareCtx.drawImage(
        img,
        sourceX, sourceY, sourceWidth, sourceHeight, // Source rectangle
        0, 0, cropSize, cropSize                     // Destination rectangle
      );
      
      // Convert to data URL and set the cropped image
      const croppedData = squareCanvas.toDataURL('image/jpeg', 0.95);
      setCroppedImage(croppedData);
    };
    
    img.src = image;
  }, [image, position.x, position.y, scale]);

  const handleUpload = async () => {
    if (!croppedImage || !token) return;

    try {
      setUploadProgress(0);
      setUploadStatus('');
      setError(null);

      // Convert base64 to blob
      const base64Data = croppedImage.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = new Array(slice.length);
        
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      const blob = new Blob(byteArrays, { type: 'image/jpeg' });
      const formData = new FormData();
      formData.append('file', blob, 'profile.jpg');

      const xhr = new XMLHttpRequest();
      xhr.open('POST', 'https://info.kissdatelove.com/rest/profile/photos/upload');
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (event) => {
        const progress = Math.round((event.loaded / event.total) * 100);
        setUploadProgress(progress);
      };

      xhr.onload = () => {
        console.log('Upload response:', xhr.status, xhr.responseText);
        if (xhr.status === 200) {
          setUploadStatus('Upload Successful');
          fetchPhotoCount();
          setImage(null);
          setCroppedImage(null);
        } else if (xhr.status === 401 || xhr.status === 403) {
          authContext.logout();
          navigate('/login');
        } else {
          setUploadStatus('Upload Failed');
          setError(`Failed to upload photo. Server responded with status ${xhr.status}`);
        }
      };

      xhr.onerror = () => {
        console.error('XHR error:', xhr);
        setUploadStatus('Upload Failed');
        setError('Network error occurred. Please check your connection.');
      };

      xhr.send(formData);
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploadStatus('Upload Failed');
      setError('Failed to upload photo. Please try again.');
    }
  };

  const photoLimit = photoLimits[memberType] || 10;
  const canUploadMore = photoCount < photoLimit;

  return (
    <div className="container mt-4">
      <div className="w-full max-w-4xl bg-white rounded-lg shadow-md p-4 mx-auto">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
            {error}
            <button
              onClick={() => setError(null)}
              className="absolute top-0 right-0 p-2 text-red-700 hover:text-red-900"
            >
              ✕
            </button>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <p>Click select, crop image and upload</p>
            <p>Drag circle around for selection - use slider to zoom</p>
          </div>

          {canUploadMore ? (
            <>
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => document.getElementById('fileInput').click()}
                  style={{ 
                    padding: '0.5rem 1rem', 
                    backgroundColor: '#0066cc', 
                    color: 'white', 
                    borderRadius: '0.25rem',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                  >
                  Select Picture
                </button>
                <input
                  id="fileInput"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <p className="font-semibold m-0">
                  You have {photoCount} uploaded pictures of {photoLimit} Picture limit
                  for {memberType === '1' ? 'free' : 'upgraded'} members.
                </p>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-4">
              <p className="font-semibold m-0">
                {photoLimit} Picture limit reached for {memberType === '1' ? 'free' : 'upgraded'} members.
              </p>
              <button 
                onClick={() => navigate('/profile/manage-photos')}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                Delete Photos
              </button>
            </div>
          )}

          {image && (
            <div className="mt-4">
              {/* Replace flex with a table-based layout */}
              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "20px 0" }}>
                <tbody>
                  <tr>
                    {/* Canvas cell */}
                    <td style={{ verticalAlign: "top" }}>
                      <div 
                        className="canvas-container"
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                      >
                        <canvas
                          ref={canvasRef}
                          width={400}
                          height={400}
                          style={{
                            backgroundColor: 'transparent',
                            borderRadius: '4px',
                            cursor: isDragging ? 'grabbing' : 'grab'
                          }}
                        />
                      </div>
                      
                      <div className="zoom-container mt-2">
                        <label className="mr-2">Zoom</label>
                        <input
                          type="range"
                          min="1"
                          max="3"
                          step="0.1"
                          value={scale}
                          onChange={(e) => setScale(parseFloat(e.target.value))}
                          className="w-full"
                        />
                      </div>
                    </td>

                    {/* Preview cell - only show if we have a cropped image */}
                    {croppedImage && (
                      <td style={{ verticalAlign: "top" }}>
                        <p className="font-medium mb-2">Preview:</p>
                        <img
                          src={croppedImage}
                          alt="Preview"
                          className="preview-image"
                          style={{
                            width: '300px',
                            height: '300px',
                            objectFit: 'contain',
                            borderRadius: '0',
                            border: '1px solid #ccc'
                          }}
                        />
                      </td>
                    )}
                  </tr>
                </tbody>
              </table>

              {/* Save button centered below both images */}
              {croppedImage && canUploadMore && (
                <div className="flex justify-center mt-4">
                  <button 
                    onClick={handleUpload}
                    style={{ 
                      padding: '0.5rem 1rem', 
                      backgroundColor: '#0066cc', 
                      color: 'white', 
                      borderRadius: '0.25rem',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    >
                    Save Picture
                  </button>
                </div>
              )}
            </div>
          )}

          {uploadProgress > 0 && (
            <div className="w-full bg-gray-200 rounded mt-4">
              <div 
                className="h-2 bg-blue-500 rounded transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}
          
          {uploadStatus && (
            <p className={`${uploadStatus === 'Upload Successful' ? 'text-green-500' : 'text-red-500'} font-semibold text-center mt-2`}>
              {uploadStatus}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePhotoUploadComponent;